/**
 * AutoFadeNavbar - Wrapper voor navbar met subtiele auto-fade
 * 
 * Gedrag:
 * - Na 5 seconden geen hover: langzaam fade naar opacity 0.3 (20s smooth)
 * - Bij hover: direct opacity 1 (0.5s snel)
 * - Hele navbar als één eenheid
 */

import * as React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { cn } from "../../lib/utils";

interface AutoFadeNavbarProps {
  children: React.ReactNode;
  className?: string;
  /**
   * Tijd in ms voordat fade begint (default: 5000ms = 5s)
   */
  fadeDelay?: number;
  /**
   * Opacity level bij gefade state (default: 0.3)
   */
  fadedOpacity?: number;
}

export const AutoFadeNavbar: React.FC<AutoFadeNavbarProps> = ({
  children,
  className,
  fadeDelay = 5000,
  fadedOpacity = 0.3,
}) => {
  const [isHovered, setIsHovered] = React.useState(false);
  const [shouldFade, setShouldFade] = React.useState(false);
  const timeoutRef = React.useRef<NodeJS.Timeout | null>(null);
  const prefersReducedMotion = useReducedMotion();

  // Start fade timer wanneer niet hover
  React.useEffect(() => {
    if (isHovered) {
      // Clear timer bij hover
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      setShouldFade(false);
      return;
    }

    // Start fade timer na delay
    timeoutRef.current = setTimeout(() => {
      setShouldFade(true);
    }, fadeDelay);

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [isHovered, fadeDelay]);

  // Bepaal target opacity
  const targetOpacity = isHovered || !shouldFade ? 1 : fadedOpacity;

  // Bepaal transition duration
  const transitionDuration = isHovered ? 0.5 : 20; // Snel in, traag uit

  return (
    <motion.div
      className={cn(className)}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onTouchStart={() => {
        // On mobile there is no hover, so a touch always restores full opacity
        setIsHovered(true);
        // Reset after a short window so the fade-out timer restarts
        setTimeout(() => setIsHovered(false), 2000);
      }}
      animate={{
        opacity: prefersReducedMotion ? 1 : targetOpacity,
      }}
      transition={{
        duration: prefersReducedMotion ? 0 : transitionDuration,
        ease: "easeOut",
      }}
    >
      {children}
    </motion.div>
  );
};
